/**
 * Where a scanned Web QR code lands, and how that link is built.
 *
 * The API only ever returns a code's `slug` — never the full URL — so the
 * dashboard composes the destination. That makes the host a dashboard-side
 * choice, picked per code when it is generated and remembered in
 * localStorage, because the create/update endpoints carry no domain field.
 *
 * These values live here rather than inside one component because two screens
 * now render the same QR: the Web QR management page and the campaign detail
 * page's QR Instance table. A second copy of the default host would mean a
 * silently wrong (dead) QR on one of them the day it changes.
 */

export interface QrDomainOption {
  label: string;
  base: string;
}

/**
 * The Luqta web app, used unless the client picked one of their own domains
 * (registered under Branding → Domains & Hosts).
 */
export const DEFAULT_QR_DOMAIN: QrDomainOption = {
  label: 'Luqta Web',
  base: 'https://web-luqta.vercel.app/q',
};

export const DEFAULT_QR_BASE = DEFAULT_QR_DOMAIN.base;

/** Path the public web app serves scanned codes from, appended to a branded host. */
export const QR_DOMAIN_PATH = '/q';

/** localStorage key prefix holding the per-code host choice, keyed by campaign. */
export const QR_BASE_STORAGE_PREFIX = 'webQrBase:';

export function composeQrShortLink(slug: string, base: string): string {
  const cleanBase = String(base || '').replace(/\/+$/, '');
  const cleanSlug = String(slug || '').replace(/^\/+/, '');
  return cleanBase && cleanSlug ? `${cleanBase}/${cleanSlug}` : '';
}

/** The slug → host map saved for one campaign, or {} when there is none. */
export function readQrBaseOverrides(contestId: number | string): Record<string, string> {
  try {
    const raw = localStorage.getItem(`${QR_BASE_STORAGE_PREFIX}${contestId}`);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Blocked or full storage costs the choice its persistence, nothing more.
    return {};
  }
}

/**
 * The URL a given instance's QR should encode.
 *
 * A host chosen for this code wins over any `short_link` the API sends: the
 * backend composes that field on the default host, so honouring it would
 * ignore the selection and produce a QR pointing at the wrong place.
 *
 * Accepts rows from either shape — qr_instance_list (which has `short_link`)
 * or qr_report_detail's instances (which have only `slug`).
 */
export function resolveQrInstanceLink(instance: any, contestId: number | string): string {
  const slug = String(instance?.slug || '').trim();
  const overrides = readQrBaseOverrides(contestId);
  const chosenBase = slug ? overrides[slug] : '';

  if (chosenBase) {
    return composeQrShortLink(slug, chosenBase);
  }

  return (
    instance?.qr_value ||
    instance?.short_link ||
    instance?.link ||
    instance?.url ||
    composeQrShortLink(slug, DEFAULT_QR_BASE)
  );
}
